import { Box, IconButton, List, ListItem, ListItemButton, ListItemText, SwipeableDrawer, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { DateTime } from 'luxon';
import type { CalendarBlockedDate, CalendarBooking, CalendarCleaning, CalendarEvent, MissingCleaning } from '../types/calendar';

interface DayDetailsDrawerProps {
    open: boolean;
    selectedDate: string | null;
    events: CalendarEvent[];
    onClose: () => void;
    onBookingClick: (booking: CalendarBooking) => void;
    onBlockedDateClick: (blockedDate: CalendarBlockedDate) => void;
    onCleaningClick: (cleaning: CalendarCleaning) => void;
    onMissingCleaningClick: (missingCleaning: MissingCleaning) => void;
}

const getEventColors = (type: CalendarEvent['type']) => {
    switch (type) {
        case 'booking':
            return {
                background: 'rgba(99, 102, 241, 0.3)',
                border: '#6366f1',
                text: '#c7d2fe',
            };
        case 'blocked-maintenance':
            return {
                background: 'rgba(239, 68, 68, 0.3)',
                border: '#ef4444',
                text: '#fecaca',
            };
        case 'blocked-personal':
            return {
                background: 'rgba(245, 158, 11, 0.3)',
                border: '#f59e0b',
                text: '#fde68a',
            };
        case 'cleaning':
            return {
                background: 'rgba(34, 197, 94, 0.3)',
                border: '#22c55e',
                text: '#bbf7d0',
            };
        case 'cleaning-unpaid':
            return {
                background: 'rgba(245, 158, 11, 0.3)',
                border: '#f59e0b',
                text: '#fde68a',
            };
        case 'missing-cleaning':
            return {
                background: 'rgba(245, 158, 11, 0.15)',
                border: '#f59e0b',
                text: '#fde68a',
            };
    }
};

const getEventTypeLabel = (type: CalendarEvent['type']) => {
    switch (type) {
        case 'booking':
            return 'Booking';
        case 'blocked-maintenance':
            return 'Maintenance';
        case 'blocked-personal':
            return 'Personal';
        case 'cleaning':
            return 'Cleaning';
        case 'cleaning-unpaid':
            return 'Unpaid Cleaning';
        case 'missing-cleaning':
            return 'Missing Cleaning';
    }
};

const DayDetailsDrawer = ({
    open,
    selectedDate,
    events,
    onClose,
    onBookingClick,
    onBlockedDateClick,
    onCleaningClick,
    onMissingCleaningClick,
}: DayDetailsDrawerProps) => {
    // Filter events for the selected date
    const dayEvents = selectedDate
        ? events.filter((event) => selectedDate >= event.startDate && selectedDate <= event.endDate)
        : [];

    const formattedDate = selectedDate
        ? DateTime.fromISO(selectedDate).toFormat('EEEE, MMMM d, yyyy')
        : '';

    const handleEventClick = (event: CalendarEvent) => {
        if (event.type === 'booking' && event.booking) {
            onBookingClick(event.booking);
        } else if (event.type === 'cleaning' && event.cleaning) {
            onCleaningClick(event.cleaning);
        } else if (event.type === 'missing-cleaning' && event.missingCleaning) {
            onMissingCleaningClick(event.missingCleaning);
        } else if (event.blockedDate) {
            onBlockedDateClick(event.blockedDate);
        }
        onClose();
    };

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            onOpen={() => {}}
            disableSwipeToOpen
            ModalProps={{ keepMounted: true }}
            PaperProps={{
                sx: {
                    backgroundColor: '#1a1a2e',
                    borderTop: '4px solid #6366f1',
                    borderRadius: 0,
                    maxHeight: '60vh',
                },
            }}
        >
            <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        pb: 2,
                        borderBottom: '2px solid #374151',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: '#fff',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            border: '2px solid #374151',
                            borderRadius: 0,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                borderColor: '#6366f1',
                            },
                        }}
                        aria-label="Close"
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* Events List */}
                {dayEvents.length === 0 ? (
                    <Typography
                        sx={{
                            color: '#9ca3af',
                            textAlign: 'center',
                            py: 4,
                            fontWeight: 600,
                        }}
                    >
                        No events on this day
                    </Typography>
                ) : (
                    <List disablePadding>
                        {dayEvents.map((event) => {
                            const colors = getEventColors(event.type);
                            const typeLabel = getEventTypeLabel(event.type);

                            return (
                                <ListItem
                                    key={`${event.type}-${event.id}`}
                                    disablePadding
                                    sx={{ mb: 1 }}
                                >
                                    <ListItemButton
                                        onClick={() => handleEventClick(event)}
                                        sx={{
                                            backgroundColor: colors.background,
                                            border: `2px solid ${colors.border}`,
                                            borderRadius: 0,
                                            minHeight: '56px',
                                            '&:hover': {
                                                backgroundColor: colors.border,
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={event.label}
                                            secondary={typeLabel}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontWeight: 700,
                                                    color: colors.text,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.02em',
                                                },
                                            }}
                                            secondaryTypographyProps={{
                                                sx: {
                                                    color: '#9ca3af',
                                                    fontSize: '0.75rem',
                                                    textTransform: 'uppercase',
                                                },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>
        </SwipeableDrawer>
    );
};

export default DayDetailsDrawer;
